import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sparkle, WarningCircle, ArrowRight, Info, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAccessGate } from "@/hooks/useAccessGate";

const EXAMPLES = [
  "Questionnaire de suivi de la qualité du sommeil chez l'adulte (10 questions, échelle Likert)",
  "Auto-évaluation de la douleur chronique lombaire post-consultation",
  "Recueil des habitudes alimentaires pour un patient diabétique de type 2",
];

export default function AITemplateDialog({ open, onOpenChange }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { isTrialExpired, gateProps } = useAccessGate();

  const submit = async (e) => {
    e.preventDefault();
    const p = prompt.trim();
    if (p.length < 8) {
      toast.error("Décris ton besoin en quelques mots (8 caractères minimum)");
      return;
    }
    setLoading(true);
    try {
      // 1. Ask the AI to draft the questionnaire
      const { data: draft } = await api.post("/ai/templates/generate", { prompt: p });
      // 2. Persist it as a real template (draft, is_validated=false) via the
      // existing endpoint so it goes through the same Pydantic validation.
      // We strip the `generated_by_ai` marker — it's informational only.
      const { generated_by_ai: _unused, ...payload } = draft; // eslint-disable-line no-unused-vars
      const { data: created } = await api.post("/templates", payload);
      toast.success("Questionnaire généré — révise et enregistre");
      onOpenChange(false);
      setPrompt("");
      nav(`/templates/${created.template_id}/edit`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "La génération a échoué";
      toast.error(String(msg).slice(0, 200));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!loading) { onOpenChange(v); if (!v) setPrompt(""); } }}>
      <DialogContent className="sm:max-w-[560px] bg-white border-[#E1E9F0]" data-testid="ai-template-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1E3A5F] flex items-center gap-2">
            <Sparkle size={20} weight="duotone" className="text-[#3A8FA5]" />
            Créer avec l'IA
          </DialogTitle>
          <DialogDescription className="text-[#5F758F]">
            Décrivez le questionnaire que vous souhaitez. L'IA propose un brouillon (titre,
            questions, scoring si pertinent) que vous pouvez ensuite éditer librement.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {isTrialExpired && (
            <div className="rounded-[10px] bg-[#FFF3D6] border border-[#F0DDA8] px-3 py-2.5 flex items-start gap-2" data-testid="ai-template-trial-expired">
              <Lock size={14} weight="duotone" className="text-[#8B2A1E] shrink-0 mt-0.5" />
              <div className="text-[12px] text-[#7A4C0B] leading-relaxed">
                <p className="font-semibold text-[#8B2A1E] mb-0.5">Période d'essai terminée</p>
                Souscrivez pour générer de nouveaux questionnaires avec l'IA.
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="ai-prompt" className="text-[13px] font-semibold text-[#1E3A5F]">
              Que voulez-vous construire ?
            </Label>
            <Textarea
              id="ai-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ex : Un questionnaire court de suivi de l'anxiété post-opératoire, 8 à 10 questions, avec un score global et une interprétation clinique en 4 niveaux."
              rows={6}
              maxLength={4000}
              disabled={loading}
              className="mt-1.5 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5] text-sm"
              data-testid="ai-template-prompt"
              autoFocus
            />
            <div className="mt-1 text-[11px] text-[#5F758F] text-right">
              {prompt.length} / 4000
            </div>
          </div>

          <div className="bg-[#F5F9FB] border border-[#E1E9F0] rounded-[10px] p-3">
            <p className="text-[11px] tracking-[0.1em] uppercase font-semibold text-[#5F758F] mb-2 flex items-center gap-1.5">
              <Info size={12} weight="duotone" /> Exemples d'inspiration
            </p>
            <ul className="space-y-1.5">
              {EXAMPLES.map((ex, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => setPrompt(ex)}
                    disabled={loading}
                    className="text-left text-[12px] text-[#3A8FA5] hover:text-[#1E3A5F] hover:underline disabled:opacity-50"
                    data-testid={`ai-template-example-${i}`}
                  >
                    « {ex} »
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-start gap-2 bg-[#FFF7ED] border border-[#F0C88F] rounded-[10px] p-3">
            <WarningCircle size={16} weight="duotone" className="text-[#C97A2E] flex-shrink-0 mt-0.5" />
            <p className="text-[12px] text-[#7A5220] leading-relaxed">
              Le brouillon généré n'est <strong>pas un questionnaire validé scientifiquement</strong>.
              Relisez chaque question avant utilisation clinique.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="rounded-[10px] border-[#E1E9F0] text-[#5F758F]"
              data-testid="ai-template-cancel"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading || prompt.trim().length < 8 || isTrialExpired}
              className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="ai-template-submit"
              {...(isTrialExpired ? gateProps({ title: "Générer" }) : {})}
            >
              {loading ? (
                <>
                  <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin mr-2" />
                  Génération en cours…
                </>
              ) : (
                <>
                  <Sparkle size={16} weight="duotone" className="mr-2" /> Générer
                  <ArrowRight size={14} className="ml-2" />
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
